import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  runTransaction,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type {
  Conversation,
  DirectMessage,
  ConversationMetadata,
  DMContact,
  SendMessageInput,
  AdminConversationView,
} from '@/types/dm';
import { getUserProfile } from './auth';
import {
  encryptionService,
  ENCRYPTED_PLACEHOLDER,
  DECRYPTING_PLACEHOLDER,
  DECRYPT_FAILED_PLACEHOLDER,
  type EncryptedEnvelope,
} from './encryption';

// Collection paths
const getConversationsPath = () => `artifacts/${appId}/conversations`;
const getMessagesPath = (conversationId: string) =>
  `artifacts/${appId}/conversations/${conversationId}/messages`;
// dm_metadata is a collection where each document is keyed by conversationId
const getUserDMMetadataPath = (userId: string) => `artifacts/${appId}/users/${userId}/dm_metadata`;
const getTypingPath = (conversationId: string, userId: string) =>
  `artifacts/${appId}/conversations/${conversationId}/typing/${userId}`;
// Push notification queue for Cloud Function processing
const getPushQueuePath = () => `artifacts/${appId}/push_queue`;

// ==================== Push Notification Helpers ====================

/**
 * Create a push notification entry for a DM message.
 * If encryption is available, the preview is encrypted and stored as
 * encryptedBody/bodyIv so the Cloud Function can decrypt it server-side.
 */
async function createDMPushNotification(data: {
  recipientId: string;
  senderId: string;
  senderName: string;
  conversationId: string;
  messageId: string;
  preview: string;
}): Promise<void> {
  const pushQueueRef = collection(db, getPushQueuePath());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queueData: Record<string, any> = {
    type: 'dm',
    recipientId: data.recipientId,
    title: `Message from ${data.senderName}`,
    body: 'New message', // Fallback if Cloud Function can't decrypt
    data: {
      conversationId: data.conversationId,
      messageId: data.messageId,
      senderId: data.senderId,
      url: `/chat/messages`,
    },
    createdAt: serverTimestamp(),
  };

  // Encrypt the preview for at-rest protection in the push_queue
  if (encryptionService.isReady() && data.preview) {
    try {
      const encrypted = await encryptionService.encryptPreview(data.conversationId, data.preview);
      queueData.encryptedBody = encrypted.ciphertext;
      queueData.bodyIv = encrypted.iv;
      queueData.bodyKeyVersion = encrypted.keyVersion;
      queueData.conversationId = data.conversationId; // Needed for key derivation
    } catch {
      // Fall through to use generic "New message" body
    }
  }

  await addDoc(pushQueueRef, queueData);
}

// ==================== Conversation Management ====================

/**
 * Find an existing conversation between two users
 */
export async function findConversation(
  userId1: string,
  userId2: string
): Promise<Conversation | null> {
  const conversationsRef = collection(db, getConversationsPath());

  // Query for conversations where both users are participants
  const q = query(conversationsRef, where('participants', 'array-contains', userId1), limit(100));

  const snapshot = await getDocs(q);

  for (const doc of snapshot.docs) {
    const data = doc.data();
    if (data.participants.includes(userId2)) {
      return { id: doc.id, ...data } as Conversation;
    }
  }

  return null;
}

/**
 * Create a new conversation between an admin/leader and a member
 */
export async function createConversation(
  initiatorId: string,
  memberId: string,
  initialMessage?: string
): Promise<string> {
  // Check if conversation already exists
  const existing = await findConversation(initiatorId, memberId);
  if (existing) {
    return existing.id;
  }

  const conversationsRef = collection(db, getConversationsPath());
  const now = serverTimestamp();

  const conversationData = {
    participants: [initiatorId, memberId],
    initiatorId,
    memberId,
    createdAt: now,
    lastMessageAt: now,
    lastMessagePreview: initialMessage || '',
    lastMessageBy: '',
    lastMessageSenderId: '',
  };

  const docRef = await addDoc(conversationsRef, conversationData);
  const conversationId = docRef.id;

  // Initialize the initiator's own metadata. The member's metadata document is created by the
  // bumpDmUnreadCount Cloud Function on the first message (users cannot write each other's), and
  // readers fall back to defaults while it does not exist.
  await initializeConversationMetadata(conversationId, initiatorId);

  // Send initial message if provided
  if (initialMessage) {
    const initiatorProfile = await getUserProfile(initiatorId);
    await sendMessage(
      conversationId,
      initiatorId,
      {
        text: initialMessage,
      },
      initiatorProfile?.displayName || 'Unknown'
    );
  }

  return conversationId;
}

/**
 * Get or create a conversation between two users
 */
export async function getOrCreateConversation(
  initiatorId: string,
  memberId: string
): Promise<string> {
  const existing = await findConversation(initiatorId, memberId);
  if (existing) {
    // Unarchive if archived
    await unarchiveConversation(existing.id, initiatorId);
    return existing.id;
  }
  return createConversation(initiatorId, memberId);
}

/**
 * Get a conversation by ID
 */
export async function getConversation(conversationId: string): Promise<Conversation | null> {
  const docRef = doc(db, getConversationsPath(), conversationId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as Conversation;
}

/**
 * Initialize conversation metadata for a user
 */
async function initializeConversationMetadata(
  conversationId: string,
  userId: string
): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);

  await setDoc(metadataRef, {
    conversationId,
    unreadCount: 0,
    lastReadAt: null,
    isArchived: false,
    isFavorite: false,
    isMuted: false,
    isBlocked: false,
  });
}

/**
 * Get conversation metadata for a user
 */
export async function getConversationMetadata(
  conversationId: string,
  userId: string
): Promise<ConversationMetadata | null> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);
  const snapshot = await getDoc(metadataRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as ConversationMetadata;
}

// ==================== Message Management ====================

/**
 * Send a message in a conversation.
 * Uses a Firestore transaction to atomically:
 * 1. Create the message
 * 2. Update conversation metadata
 * 3. Increment recipient's unread count
 *
 * This prevents race conditions where concurrent messages could cause
 * incorrect unread counts or inconsistent conversation state.
 */
export async function sendMessage(
  conversationId: string,
  senderId: string,
  message: SendMessageInput,
  senderName: string
): Promise<string> {
  const conversationRef = doc(db, getConversationsPath(), conversationId);

  // Get conversation to find the other participant (read before transaction)
  const conversation = await getConversation(conversationId);
  if (!conversation) {
    throw new Error('Conversation not found');
  }

  const recipientId = conversation.participants.find((p) => p !== senderId);
  if (!recipientId) {
    throw new Error('Recipient not found');
  }

  // Check if sender is blocked by recipient (only for member replies)
  const recipientMetadata = await getConversationMetadata(conversationId, recipientId);
  if (recipientMetadata?.isBlocked && senderId === conversation.memberId) {
    throw new Error('You have been blocked from this conversation');
  }

  const preview = message.text.substring(0, 100) + (message.text.length > 100 ? '...' : '');
  // Block sending if encryption is not ready (never fall back to unencrypted for new messages)
  if (!encryptionService.isReady()) {
    throw new Error('Encryption service not ready. Please wait and try again.');
  }

  // Build the encrypted envelope containing all sensitive fields
  const envelope: EncryptedEnvelope = {
    text: message.text,
    attachments: message.attachments || [],
    ...(message.quotedMessageId && { quotedMessageId: message.quotedMessageId }),
    ...(message.quotedMessage && { quotedMessage: message.quotedMessage }),
  };

  const encryptedMessage = await encryptionService.encryptMessage(conversationId, envelope);
  const encryptedPreview = await encryptionService.encryptPreview(conversationId, preview);

  // Use a transaction for atomic message creation and metadata updates
  const messageId = await runTransaction(db, async (transaction) => {
    // Verify conversation still exists inside transaction
    const conversationSnap = await transaction.get(conversationRef);
    if (!conversationSnap.exists()) {
      throw new Error('Conversation not found');
    }

    // Create a new message document reference
    const messagesRef = collection(db, getMessagesPath(conversationId));
    const newMessageRef = doc(messagesRef);

    // Build encrypted message data
    const messageData: Record<string, unknown> = {
      conversationId,
      senderId,
      senderName,
      timestamp: serverTimestamp(),
      readAt: null,
      isDeleted: false,
      // Encryption fields
      encrypted: true,
      ciphertext: encryptedMessage.ciphertext,
      iv: encryptedMessage.iv,
      keyVersion: encryptedMessage.keyVersion,
      hasAttachments: (message.attachments?.length ?? 0) > 0,
      // Include clientId for correlating optimistic updates with server messages
      ...(message.clientId && { clientId: message.clientId }),
    };

    // Create message
    transaction.set(newMessageRef, messageData);

    // Update conversation with encrypted preview
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conversationUpdate: Record<string, any> = {
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: ENCRYPTED_PLACEHOLDER, // Fallback for admin view
      lastMessageBy: senderName,
      lastMessageSenderId: senderId,
    };

    if (encryptedPreview) {
      conversationUpdate.encryptedPreview = encryptedPreview.ciphertext;
      conversationUpdate.previewIv = encryptedPreview.iv;
      conversationUpdate.previewKeyVersion = encryptedPreview.keyVersion;
    }

    transaction.update(conversationRef, conversationUpdate);

    // The recipient's unread count is NOT written here. The bumpDmUnreadCount Cloud Function
    // increments it when the message is created (it honours mute/block), because the rules do
    // not let one user write another user's dm_metadata.

    return newMessageRef.id;
  });

  // Create push notification for recipient (outside transaction - can fail independently)
  if (!recipientMetadata?.isMuted) {
    try {
      await createDMPushNotification({
        recipientId,
        senderId,
        senderName,
        conversationId,
        messageId,
        preview,
      });
    } catch (error) {
      // Don't fail the message send if push notification fails
      console.error('Failed to create push notification:', error);
    }
  }

  return messageId;
}

/**
 * Mark all messages in a conversation as read for a user.
 * Marks individual messages first, then sets unread count to 0.
 * This ordering prevents a race where a new message arrives between
 * zeroing the count and marking messages, causing an incorrect count.
 */
export async function markAsRead(conversationId: string, userId: string): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);

  // Update readAt on unread messages sent by the other user FIRST
  const conversation = await getConversation(conversationId);
  if (!conversation) {
    // Still set metadata even if conversation lookup fails
    await setDoc(
      metadataRef,
      { conversationId, unreadCount: 0, lastReadAt: serverTimestamp() },
      { merge: true }
    );
    return;
  }

  const messagesRef = collection(db, getMessagesPath(conversationId));
  const q = query(messagesRef, where('senderId', '!=', userId), where('readAt', '==', null));

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, { readAt: Timestamp.now() });
  });

  await batch.commit();

  // THEN set metadata unreadCount to 0 after messages are marked read
  await setDoc(
    metadataRef,
    {
      conversationId,
      unreadCount: 0,
      lastReadAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Soft delete a message (mark as deleted)
 */
export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  const messageRef = doc(db, getMessagesPath(conversationId), messageId);
  await updateDoc(messageRef, {
    isDeleted: true,
    text: '[Message deleted]',
    attachments: [],
    // Clear encryption fields on delete
    ciphertext: null,
    iv: null,
    keyVersion: null,
    hasAttachments: false,
  });
}

/**
 * Get messages for a conversation
 */
export async function getMessages(
  conversationId: string,
  limitCount: number = 50
): Promise<DirectMessage[]> {
  const messagesRef = collection(db, getMessagesPath(conversationId));
  const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(limitCount));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as DirectMessage).reverse();
}

// ==================== Subscriptions (Real-time) ====================

/**
 * Check if an archived conversation should be auto-unarchived
 * because a new incoming message arrived from the other user.
 */
function checkAutoUnarchive(
  metadata: ConversationMetadata | null,
  conversation: Conversation,
  userId: string
): void {
  if (
    !metadata?.isArchived ||
    !conversation.lastMessageSenderId ||
    conversation.lastMessageSenderId === userId
  ) {
    return;
  }
  const lastReadTime = metadata.lastReadAt?.toMillis() ?? 0;
  const lastMessageTime = conversation.lastMessageAt?.toMillis() ?? 0;
  if (lastMessageTime > lastReadTime) {
    unarchiveConversation(conversation.id, userId).catch(console.error);
    metadata.isArchived = false;
  }
}

/**
 * Subscribe to conversations for a user
 */
export function subscribeToConversations(
  userId: string,
  callback: (contacts: DMContact[]) => void
): () => void {
  const conversationsRef = collection(db, getConversationsPath());
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc')
  );

  // Track the latest snapshot to prevent stale callbacks from overwriting newer data
  let latestSnapshotId = 0;
  // Track if subscription has been cancelled to prevent callbacks after unsubscribe
  let isUnsubscribed = false;

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      // Skip if already unsubscribed
      if (isUnsubscribed) return;

      // Increment snapshot ID to track this callback
      const currentSnapshotId = ++latestSnapshotId;

      // Process all conversations in parallel for better performance
      const contactPromises = snapshot.docs.map(async (docSnap) => {
        // Check if unsubscribed before each async operation
        if (isUnsubscribed) return null;

        try {
          const conversation = { id: docSnap.id, ...docSnap.data() } as Conversation;
          const otherUserId = conversation.participants.find((p) => p !== userId);

          if (!otherUserId) return null;

          // Skip empty conversations for non-initiator (member) users.
          // When an admin opens "New Chat" and selects a user, the conversation
          // document is created immediately in Firestore — but no message has been
          // sent yet. Without this guard the member would see a ghost conversation
          // with "No messages yet" even though the admin hasn't actually written
          // anything. We hide it until the first real message is sent.
          if (conversation.memberId === userId && !conversation.lastMessageSenderId) {
            return null;
          }

          // Get metadata and other user profile in parallel
          const [metadata, otherUser] = await Promise.all([
            getConversationMetadata(conversation.id, userId),
            getUserProfile(otherUserId),
          ]);

          // Check again after async operations
          if (isUnsubscribed) return null;

          if (!otherUser) return null;

          // Auto-unarchive if there is a new incoming message from the other user
          checkAutoUnarchive(metadata, conversation, userId);

          // Decrypt the conversation preview if encrypted
          if (
            conversation.encryptedPreview &&
            conversation.previewIv &&
            encryptionService.isReady()
          ) {
            try {
              const decryptedPreview = await encryptionService.decryptPreview(conversation.id, {
                ciphertext: conversation.encryptedPreview,
                iv: conversation.previewIv,
                keyVersion: conversation.previewKeyVersion || 1,
              });
              conversation.lastMessagePreview = decryptedPreview;
            } catch {
              // Keep the fallback "[Encrypted]" placeholder
            }
          }

          return {
            conversation,
            metadata: metadata || {
              conversationId: conversation.id,
              unreadCount: 0,
              lastReadAt: null,
              isArchived: false,
              isFavorite: false,
              isMuted: false,
              isBlocked: false,
            },
            otherUser,
            isTyping: false, // Will be updated by typing subscription
          } as DMContact;
        } catch (error) {
          // Don't log errors if unsubscribed
          if (!isUnsubscribed) {
            console.error(`[DM] Error processing conversation ${docSnap.id}:`, error);
          }
          return null;
        }
      });

      // Wait for all promises and filter out nulls
      Promise.all(contactPromises)
        .then((results) => {
          // Check if unsubscribed or if this is a stale snapshot
          if (isUnsubscribed || currentSnapshotId !== latestSnapshotId) {
            return;
          }

          const contacts = results.filter((c): c is DMContact => c !== null);
          callback(contacts);
        })
        .catch((error) => {
          if (!isUnsubscribed) {
            console.error('[DM] Error processing contacts:', error);
          }
        });
    },
    (error) => {
      if (!isUnsubscribed) {
        console.error('[DM] Conversation subscription error:', error);
      }
    }
  );

  // Return cleanup function that marks subscription as cancelled
  return () => {
    isUnsubscribed = true;
    unsubscribe();
  };
}

/**
 * Subscribe to messages in a conversation.
 *
 * Handles both encrypted and unencrypted (legacy) messages via dual-read mode.
 * Encrypted messages are decrypted in background after each snapshot, with
 * placeholder text shown while decryption is in progress.
 */
export function subscribeToMessages(
  conversationId: string,
  callback: (messages: DirectMessage[]) => void
): () => void {
  const messagesRef = collection(db, getMessagesPath(conversationId));
  const q = query(messagesRef, orderBy('timestamp', 'asc'));

  let latestSnapshotId = 0;

  return onSnapshot(
    q,
    (snapshot) => {
      const currentSnapshotId = ++latestSnapshotId;

      // Build raw messages, showing placeholder for encrypted ones
      const rawMessages = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // For encrypted messages, show placeholder until decrypted
          text: data.encrypted && !data.isDeleted ? DECRYPTING_PLACEHOLDER : data.text || '',
          attachments: data.encrypted && !data.isDeleted ? [] : data.attachments || [],
        } as DirectMessage;
      });

      // Immediately provide raw messages so UI is responsive
      callback(rawMessages);

      // Check if any messages need decryption
      const hasEncrypted = snapshot.docs.some((d) => d.data().encrypted && !d.data().isDeleted);
      if (!hasEncrypted || !encryptionService.isReady()) return;

      // Decrypt encrypted messages in background
      Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();

          // Pass through unencrypted (legacy) and deleted messages
          if (!data.encrypted || data.isDeleted) {
            return { id: docSnap.id, ...data } as DirectMessage;
          }

          try {
            const envelope = await encryptionService.decryptMessage(conversationId, {
              ciphertext: data.ciphertext,
              iv: data.iv,
              keyVersion: data.keyVersion,
            });

            return {
              id: docSnap.id,
              ...data,
              text: envelope.text,
              attachments: envelope.attachments || [],
              quotedMessageId: envelope.quotedMessageId || data.quotedMessageId,
              quotedMessage: envelope.quotedMessage || data.quotedMessage,
            } as DirectMessage;
          } catch (error) {
            console.error(`[DM] Decryption failed for message ${docSnap.id}:`, error);
            return {
              id: docSnap.id,
              ...data,
              text: DECRYPT_FAILED_PLACEHOLDER,
              attachments: [] as DirectMessage['attachments'],
            } as DirectMessage;
          }
        })
      )
        .then((decryptedMessages) => {
          // Only update if this is still the latest snapshot
          if (currentSnapshotId === latestSnapshotId) {
            callback(decryptedMessages);
          }
        })
        .catch((error) => {
          console.error('[DM] Background decryption failed:', error);
        });
    },
    (error) => {
      console.error('[DM] Message subscription error:', error);
    }
  );
}

/**
 * Subscribe to typing status in a conversation
 */
export function subscribeToTyping(
  conversationId: string,
  otherUserId: string,
  callback: (isTyping: boolean) => void
): () => void {
  const typingRef = doc(db, getTypingPath(conversationId, otherUserId));

  return onSnapshot(typingRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(false);
      return;
    }

    const data = snapshot.data();
    const typingTimestamp = data.timestamp as Timestamp;

    // Consider typing stale after 5 seconds
    const isRecent = typingTimestamp && Date.now() - typingTimestamp.toMillis() < 5000;

    callback(data.isTyping && isRecent);
  });
}

/**
 * Subscribe to total unread count for a user
 */
export function subscribeToUnreadCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  const metadataRef = collection(db, getUserDMMetadataPath(userId));

  return onSnapshot(metadataRef, (snapshot) => {
    let total = 0;
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (!data.isArchived && !data.isMuted) {
        total += data.unreadCount || 0;
      }
    });
    callback(total);
  });
}

// ==================== Typing Indicators ====================

/**
 * Set typing status for a user in a conversation
 */
export async function setTypingStatus(
  conversationId: string,
  userId: string,
  isTyping: boolean
): Promise<void> {
  const typingRef = doc(db, getTypingPath(conversationId, userId));

  await setDoc(typingRef, {
    isTyping,
    userId,
    timestamp: serverTimestamp(),
  });
}

// ==================== Conversation Actions ====================

/**
 * Archive a conversation for a user
 */
export async function archiveConversation(conversationId: string, userId: string): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);
  await setDoc(metadataRef, { conversationId, isArchived: true }, { merge: true });
}

/**
 * Unarchive a conversation for a user
 */
export async function unarchiveConversation(conversationId: string, userId: string): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);
  await setDoc(metadataRef, { conversationId, isArchived: false }, { merge: true });
}

/**
 * Toggle favorite status for a conversation
 */
export async function toggleFavorite(conversationId: string, userId: string): Promise<boolean> {
  const metadata = await getConversationMetadata(conversationId, userId);
  const newValue = !metadata?.isFavorite;

  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);
  await setDoc(metadataRef, { conversationId, isFavorite: newValue }, { merge: true });

  return newValue;
}

/**
 * Mute/unmute a conversation for a user
 */
export async function muteConversation(
  conversationId: string,
  userId: string,
  muted: boolean
): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(userId), conversationId);
  await setDoc(metadataRef, { conversationId, isMuted: muted }, { merge: true });
}

/**
 * Block/unblock a user from replying in a conversation
 * Only admin/leaders can block members
 */
export async function blockUser(
  conversationId: string,
  adminId: string,
  blocked: boolean
): Promise<void> {
  const metadataRef = doc(db, getUserDMMetadataPath(adminId), conversationId);
  await setDoc(metadataRef, { conversationId, isBlocked: blocked }, { merge: true });
}

// ==================== Admin Functions ====================

/**
 * Get all conversations for admin oversight
 */
export async function getAllConversations(): Promise<AdminConversationView[]> {
  const conversationsRef = collection(db, getConversationsPath());
  const q = query(conversationsRef, orderBy('lastMessageAt', 'desc'), limit(100));

  const snapshot = await getDocs(q);
  const views: AdminConversationView[] = [];

  for (const docSnap of snapshot.docs) {
    const conversation = { id: docSnap.id, ...docSnap.data() } as Conversation;

    const [initiator, member] = await Promise.all([
      getUserProfile(conversation.initiatorId),
      getUserProfile(conversation.memberId),
    ]);

    if (!initiator || !member) continue;

    // Get message count
    const messagesRef = collection(db, getMessagesPath(conversation.id));
    const messagesSnapshot = await getDocs(query(messagesRef, limit(500)));

    views.push({
      conversation,
      initiator,
      member,
      messageCount: messagesSnapshot.size,
      lastActivity: conversation.lastMessageAt,
    });
  }

  return views;
}

/**
 * Get conversations for a specific admin/leader
 */
export async function getConversationsForAdmin(adminId: string): Promise<Conversation[]> {
  const conversationsRef = collection(db, getConversationsPath());
  const q = query(
    conversationsRef,
    where('initiatorId', '==', adminId),
    orderBy('lastMessageAt', 'desc'),
    limit(100)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Conversation);
}

/**
 * Get archived conversations for a user
 */
export async function getArchivedConversations(userId: string): Promise<DMContact[]> {
  const metadataRef = collection(db, getUserDMMetadataPath(userId));
  const q = query(metadataRef, where('isArchived', '==', true), limit(100));

  const snapshot = await getDocs(q);
  const contacts: DMContact[] = [];

  for (const docSnap of snapshot.docs) {
    const metadata = docSnap.data() as ConversationMetadata;
    const conversation = await getConversation(metadata.conversationId);

    if (!conversation) continue;

    const otherUserId = conversation.participants.find((p) => p !== userId);
    if (!otherUserId) continue;

    const otherUser = await getUserProfile(otherUserId);
    if (!otherUser) continue;

    contacts.push({
      conversation,
      metadata,
      otherUser,
      isTyping: false,
    });
  }

  return contacts;
}

/**
 * Get favorite conversations for a user
 */
export async function getFavoriteConversations(userId: string): Promise<DMContact[]> {
  const metadataRef = collection(db, getUserDMMetadataPath(userId));
  const q = query(
    metadataRef,
    where('isFavorite', '==', true),
    where('isArchived', '==', false),
    limit(100)
  );

  const snapshot = await getDocs(q);
  const contacts: DMContact[] = [];

  for (const docSnap of snapshot.docs) {
    const metadata = docSnap.data() as ConversationMetadata;
    const conversation = await getConversation(metadata.conversationId);

    if (!conversation) continue;

    const otherUserId = conversation.participants.find((p) => p !== userId);
    if (!otherUserId) continue;

    const otherUser = await getUserProfile(otherUserId);
    if (!otherUser) continue;

    contacts.push({
      conversation,
      metadata,
      otherUser,
      isTyping: false,
    });
  }

  return contacts;
}
